<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { boardApi } from '$lib/api/board';
  import { getErrorMessage } from '$lib/api/client';
  import { myDayOpenCards, myDayCompleted } from '$lib/utils/myDay';
  import { route } from '$lib/state/route.svelte';
  import { session } from '$lib/state/session.svelte';
  import { t } from '$lib/i18n';
  import type { BoardCard, BoardUpdateStatus } from '$lib/types';
  import BoardDueBadge from '$lib/components/ui/BoardDueBadge.svelte';
  import BoardCardSourceBadge from '$lib/components/ui/BoardCardSourceBadge.svelte';
  import BoardUpdatesSheet from './BoardUpdatesSheet.svelte';

  let { today }: { today: string } = $props();
  const client = useQueryClient();
  const query = createQuery(() => ({ queryKey: ['board'], queryFn: () => boardApi.list() }));
  const cards = $derived((query.data ?? []).filter((card) => card.user_id === session.me?.id));
  const open = $derived(myDayOpenCards(cards, today));
  const completed = $derived(myDayCompleted(cards, today));
  let expanded = $state(false);
  let title = $state('');
  let saving = $state(false);
  let error = $state('');
  let updateCard = $state<BoardCard | null>(null);
  let updateStatus = $state<BoardUpdateStatus>('on_track');
  const visible = $derived(expanded ? open : open.slice(0, 3));

  function refresh() { void client.invalidateQueries({ queryKey: ['board'] }); }
  function update(card: BoardCard, status: BoardUpdateStatus) {
    updateStatus = status;
    updateCard = card;
  }
  async function start(card: BoardCard) {
    if (saving) return;
    saving = true; error = '';
    try { await boardApi.update(card.id, { col: 'doing' }); refresh(); }
    catch (err) { error = getErrorMessage(err); }
    finally { saving = false; }
  }
  async function create() {
    if (!title.trim() || saving) return;
    saving = true; error = '';
    try { await boardApi.create({ title: title.trim(), col: 'todo' }); title = ''; refresh(); }
    catch (err) { error = getErrorMessage(err); }
    finally { saving = false; }
  }
</script>

<section class="tym-card mb-5 p-4 md:p-6" aria-labelledby="my-day-tasks">
  <div class="flex flex-wrap items-start justify-between gap-3">
    <div>
      <h2 id="my-day-tasks" class="font-heading text-xl font-bold text-navy">{t('myDay.title')}</h2>
      <p class="mt-1 text-sm text-dark-gray">{t('myDay.hint')}</p>
    </div>
    <button type="button" class="btn-secondary !hidden min-h-[44px] sm:!inline-flex" onclick={() => route.push({ tab: 'board' })}>{t('dashboard.viewBoard')}</button>
  </div>
  {#if query.isPending}
    <p class="py-6 text-sm text-dark-gray" role="status">{t('common.loading')}</p>
  {:else if query.isError}
    <div class="tym-error-state mt-4" role="alert">
      <p>{t('myDay.loadError')}</p>
      <button type="button" class="btn-secondary mt-3" onclick={() => query.refetch()}>{t('common.retry')}</button>
    </div>
  {:else}
    <div class="my-4 flex flex-wrap gap-x-3 gap-y-1 rounded-xl bg-surface-2 p-3 text-sm" aria-live="polite">
      <span class="font-semibold text-toyama-teal">{t('myDay.completed', { n: completed.length })}</span>
      <span class="text-navy">{t('myDay.open', { n: open.length })}</span>
      <p class="sr-only">{t('myDay.progressHint')}</p>
    </div>
    {#if open.length === 0}
      <div class="py-4">
        <h3 class="font-semibold text-navy">{t('dashboard.noOpenTasks')}</h3>
        <p class="mt-1 text-sm text-dark-gray">{t('myDay.emptyHint')}</p>
      </div>
    {:else}
      <ol class="space-y-3">
        {#each visible as card, index (card.id)}
          <li class="min-w-0 rounded-xl border border-toyama-cloudy-gray p-3 md:p-4" class:bg-surface-2={index === 0}>
            {#if index === 0}<p class="mb-2 text-xs font-bold uppercase tracking-wider text-toyama-teal">{t('myDay.next')}</p>{/if}
            <button type="button" class="min-h-[44px] w-full break-words text-left font-semibold text-navy hover:underline" onclick={() => route.push({ tab: 'board', sub: 'card', cardId: card.id })}>{card.title}</button>
            <div class="mt-1 flex flex-wrap items-center gap-2 text-xs text-dark-gray">
              <span>{t(`board.column.${card.col}`)}</span>
              <BoardDueBadge {card} />
              <BoardCardSourceBadge {card} />
            </div>
            {#if card.col === 'blocked' && card.blocked_reason}<p class="mt-2 break-words text-sm text-dark-gray">{card.blocked_reason}</p>{/if}
            <div class="mt-3 flex flex-wrap gap-2">
              {#if card.col === 'todo'}
                <button type="button" class="btn-primary min-h-[44px]" disabled={saving} onclick={() => start(card)} aria-label={`${t('myDay.start')}: ${card.title}`}>{t('myDay.start')}</button>
              {:else}
                <button type="button" class="btn-primary min-h-[44px]" onclick={() => update(card, card.col === 'blocked' ? 'blocked' : 'on_track')} aria-label={`${t('myDay.update')}: ${card.title}`}>{t('myDay.update')}</button>
              {/if}
              <button type="button" class="btn-secondary min-h-[44px]" onclick={() => update(card, 'done')} aria-label={`${t('myDay.finish')}: ${card.title}`}>{t('myDay.finish')}</button>
              {#if card.col !== 'blocked'}<button type="button" class="btn-secondary min-h-[44px]" onclick={() => update(card, 'waiting')} aria-label={`${t('myDay.help')}: ${card.title}`}>{t('myDay.help')}</button>{/if}
            </div>
          </li>
        {/each}
      </ol>
      {#if open.length > 3}<button type="button" class="btn-secondary mt-3 min-h-[44px]" aria-expanded={expanded} onclick={() => expanded = !expanded}>{expanded ? t('myDay.showLess') : t('myDay.showAll', { n: open.length })}</button>{/if}
    {/if}
    <form class="mt-4 flex flex-wrap gap-2 border-t border-toyama-cloudy-gray pt-4" onsubmit={(event) => { event.preventDefault(); create(); }}>
      <label class="min-w-0 flex-1 basis-48 text-sm font-medium text-navy">{t('myDay.add')}<input class="input mt-1 min-h-[44px] w-full" bind:value={title} maxlength="200" required /></label>
      <button type="submit" class="btn-secondary min-h-[44px] self-end" disabled={saving || !title.trim()}>{saving ? t('common.saving') : t('board.addCard')}</button>
    </form>
  {/if}
  {#if error}<p class="tym-error-state mt-3" role="alert">{error}</p>{/if}
  <button type="button" class="btn-secondary mt-4 min-h-[44px] sm:!hidden" onclick={() => route.push({ tab: 'board' })}>{t('dashboard.viewBoard')}</button>
</section>

{#if updateCard}
  {#key updateCard.id}
    <BoardUpdatesSheet card={updateCard} initialStatus={updateStatus} initialText={t(`board.quick.text.${updateStatus}`)} onClose={() => updateCard = null} onSaved={() => { refresh(); updateCard = null; }} />
  {/key}
{/if}
